import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SigmaIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { ProvenanceBadge } from '../trust/ProvenanceBadge';
export interface RecommendationItem {
  id: string;
  title: string;
  meta: string;
  reason: string;
  to?: string;
  fallback: boolean;
}
interface RecommendationPanelProps {
  title: string;
  description?: string;
  items: RecommendationItem[];
  layout?: 'list' | 'grid';
}

/**
 * Renders ranked suggestions with the reason always visible. When the user has
 * withdrawn the signals a suggestion needed, the item is marked as ranked by
 * popularity instead of quietly pretending to be personalized.
 */
export function RecommendationPanel({
  title,
  description,
  items,
  layout = 'list'
}: RecommendationPanelProps) {
  if (items.length === 0) return null;
  const anyFallback = items.some(item => item.fallback);
  return <Panel title={title} description={description} action={<ProvenanceBadge provenance="derived" short data-id="element-2216" data-mp="7j3xea7aoh0s" />} bodyClassName={layout === 'grid' ? 'p-0' : 'p-0'} data-id="element-2215" data-mp="xh5kydohdv48">
      <ul className={layout === 'grid' ? 'grid gap-px bg-line sm:grid-cols-3' : 'divide-y divide-line'} data-id="element-2217" data-mp="3itq8u7rnkkc">
        {items.map(item => {
        const body = <>
              <span className="min-w-0 flex-1" data-id="element-2218" data-mp="cg466svj3xvl">
                <span className="block text-[13px] font-semibold text-ink" data-id="element-2219" data-mp="r6bdccg35ugz">{item.title}</span>
                <span className="cz-num mt-0.5 block text-[12px] text-ink-muted" data-id="element-2220" data-mp="m7gu59e7i3wc">{item.meta}</span>
                <span className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-5 text-ink-subtle" data-id="element-2221" data-mp="1079n7bd7gw7h">
                  <SigmaIcon className="mt-0.5 h-3 w-3 shrink-0" data-id="element-2222" data-mp="v8esbb1yrh70" />
                  {item.reason}
                </span>
              </span>
              {item.to && <ArrowRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-2223" data-mp="31z4p4z9ntmci" />}
            </>;
        return <li key={item.id} className={layout === 'grid' ? 'bg-surface' : ''} data-id="element-2224" data-mp="10wdemtk1ivlr">
              {item.to ? <Link to={item.to} className="flex items-start gap-3 px-5 py-3.5 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-2225" data-mp="15v83od8lpgeb">
                  {body}
                </Link> : <div className="flex items-start gap-3 px-5 py-3.5" data-id="element-2226" data-mp="1exhc8xkb6zp2">{body}</div>}
            </li>;
      })}
      </ul>
      {anyFallback && <div className="border-t border-line px-5 py-2.5" data-id="element-2227" data-mp="1otaqqmhl939h">
          <Badge tone="neutral" data-id="element-2228" data-mp="1tl2fcrh6oywz">Ranked by popularity — personalization signals withdrawn</Badge>
        </div>}
    </Panel>;
}